import { Box } from "@outschool/backpack";
import { ClassDetailsNoSectionsQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { LazyBox } from "@outschool/ui-components-shared";
import {
  Container,
  CurvedGradientSection,
  ErrorBoundary,
  ImpressionTracking,
  Loading,
  PageWidthSection,
} from "@outschool/ui-legacy-component-library";
import React from "react";

import OutschoolInAction from "../HomePage/OutschoolInAction";
import IncrementalLoader from "../IncrementalLoader";
import {
  OtherListingsByTeacherCarousel,
  SimilarListingsCarousel,
  VisitHistoryListingsCarousel,
} from "../Listings";
import { TopicBanner } from "../Topics";
import { shortenedLeaderName } from "../utils/User";

interface ClassCarouselsProps {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: any;
  currentUser?: any;
  moreLikeThisRef: React.MutableRefObject<null>;
  isRestricted: boolean;
  randomPopularCategories?: {
    title: string;
    basePath?: string;
    slug?: string;
  }[];
}

export default function ClassCarousels({
  activity,
  leader,
  currentUser,
  moreLikeThisRef,
  isRestricted,
  randomPopularCategories,
}: ClassCarouselsProps) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\ClassCarousels"
  );
  let rowNum = 0;
  const hasOtherListings = leader.publishedClassCount > 1;

  const otherListingsByTeacher = !isRestricted && (
    <OtherListingsByTeacherCarousel
      key="otherListingsByTeacher"
      activity={activity}
      leaderName={shortenedLeaderName(leader?.name)}
      carouselRowNum={rowNum++}
    />
  );

  const relatedCategories =
    activity?.relatedCategories?.map(c => ({
      ...c,
      title: c?.nameTitleCased || "",
    })) ?? [];

  const relatedCategoriesBanner = !isRestricted &&
    relatedCategories.length > 0 && (
      <Container key="relatedCategories" overflowHidden>
        <TopicBanner
          titleText={t`Related Categories`}
          topics={relatedCategories}
        />
      </Container>
    );

  const topicBanner = React.useMemo(() => {
    return (
      !isRestricted && (
        <Container key="topicBanner" overflowHidden>
          <TopicBanner topics={randomPopularCategories} />
        </Container>
      )
    );
  }, [isRestricted, randomPopularCategories]);

  const moreLikeThis = !isRestricted && (
    <Box ref={moreLikeThisRef} key="moreLikeThis">
      <SimilarListingsCarousel
        activity={activity}
        title={t`More like this`}
        trackingProperties={{
          similar_listings_carousel_type: "more_like_this",
        }}
        algorithm="tfidf"
        carouselRowNum={rowNum++}
      />
    </Box>
  );

  return (
    <ErrorBoundary>
      <LazyBox
        rootMargin="250px 0px"
        placeholder={
          <PageWidthSection sx={{ height: 500 }}>
            <Loading />
          </PageWidthSection>
        }
      >
        <IncrementalLoader rootMargin="250px 0px">
          {/* topic banner to appear after the first non-empty carousel */}
          {hasOtherListings
            ? [otherListingsByTeacher, moreLikeThis]
            : [moreLikeThis]}
          {!isRestricted && (
            <SimilarListingsCarousel
              key="familiesAlsoLiked"
              activity={activity}
              title={t`Families also liked`}
              trackingProperties={{
                similar_listings_carousel_type: "families_also_liked",
              }}
              algorithm="popularMBA"
              carouselRowNum={rowNum++}
            />
          )}
          {!currentUser && (
            <CurvedGradientSection>
              <Box>
                <ImpressionTracking
                  trackingLabel="how_it_works_video"
                  uniqueId="how_it_works_video"
                  trackingEventName="how_it_works_video_played"
                >
                  {({ setNode, trackTouch }) => (
                    <OutschoolInAction
                      // @ts-ignore TS(2322): Type 'Dispatch<HTMLElement>' is not assignable to ... Remove this comment to see the full error message
                      ref={setNode}
                      trackTouch={trackTouch}
                    />
                  )}
                </ImpressionTracking>
              </Box>
            </CurvedGradientSection>
          )}
          {!isRestricted && (
            <VisitHistoryListingsCarousel
              currentActivity={activity}
              carouselRowNum={rowNum++}
              leftAlign
              headingSx={{ textAlign: "left" }}
            />
          )}
          {[relatedCategoriesBanner, topicBanner]}
        </IncrementalLoader>
      </LazyBox>
    </ErrorBoundary>
  );
}
